import { keepPreviousData, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useOrpc } from '@/hooks/useClient'
import type { OrpcClient } from '@/orpc/client'

dayjs.extend(relativeTime)

export type TaskInfo = Awaited<ReturnType<OrpcClient['getTask']>>['data']
export type TaskRunInfo = Awaited<ReturnType<OrpcClient['getTaskRun']>>['data']

/** The all-tasks wildcard slug accepted by GET /v2/tasks/:slug/runs. */
export const ALL_TASKS = '-'

/** A run currently occupying (or waiting for) an executor. */
export function isLiveRunStatus(status: TaskRunInfo['status']): boolean {
  return status === 'pending' || status === 'started' || status === 'cancel'
}

export const RUN_STATUS_COLORS: Record<TaskRunInfo['status'], string> = {
  paused: 'violet',
  pending: 'gray',
  started: 'blue',
  cancel: 'orange',
  cancelled: 'yellow',
  completed: 'green',
  failed: 'red',
}

export type FollowupAction = TaskInfo['followupAction']

/** Select options for task.followup_action / a run's followup_action override. */
export const FOLLOWUP_ACTION_OPTIONS: { value: FollowupAction; label: string }[] = [
  { value: 'run', label: 'run — launch follow-up tasks' },
  { value: 'pause', label: 'pause — create follow-up runs paused' },
  { value: 'ignore', label: 'ignore — do not create follow-up runs' },
]

/** Per-item outcome colors for the followup list in run details. */
export const FOLLOWUP_STATUS_COLORS: Record<
  NonNullable<TaskRunInfo['followup']>[number]['status'],
  string
> = {
  created: 'green',
  ignored: 'gray',
  invalid: 'red',
}

export function useTasks() {
  const orpc = useOrpc()
  return useQuery(
    orpc.listTasks.queryOptions({
      input: { count: 100 },
      refetchInterval: 30_000,
    }),
  )
}

export function useTaskRuns(slug: string) {
  const orpc = useOrpc()
  return useQuery(
    orpc.listTaskRuns.queryOptions({
      input: { slug, count: 50 },
      refetchInterval: 5_000,
      placeholderData: keepPreviousData,
    }),
  )
}

export type RunSelection = { slug: string; id: number }

/** One run incl. full log; keeps polling while the run is live. */
export function useTaskRun(selection: RunSelection | null) {
  const orpc = useOrpc()
  return useQuery(
    orpc.getTaskRun.queryOptions({
      input: { slug: selection?.slug ?? '', id: selection?.id ?? 0 },
      enabled: selection !== null,
      refetchInterval: (query) => {
        const status = query.state.data?.data.status
        return status !== undefined && isLiveRunStatus(status) ? 3_000 : false
      },
    }),
  )
}

export function formatDateTime(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : '—'
}

/** Human-readable distance from now: 'in an hour', '2 days ago', 'a few seconds ago'. */
export function formatRelative(iso: string): string {
  return dayjs(iso).fromNow()
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

/** started→finished for terminal runs, started→now for live ones; null before start. */
export function runDuration(run: Pick<TaskRunInfo, 'startedAt' | 'finishedAt'>): string | null {
  if (!run.startedAt) return null
  const end = run.finishedAt ? new Date(run.finishedAt).getTime() : Date.now()
  return formatDuration(end - new Date(run.startedAt).getTime())
}
